import {React, ReactResponsiveCarousel, useLocalStore, observer, classNames, StyledProps, useEffect, stringify, ImageAnnotationComponent, Fragment, uiConstants, Observer,Popover2} from '@api'
import {ImageWrapperDefinition} from '@api/graphql'
import {Icon, Menu, MenuDivider, MenuItem, Spinner} from '@blueprintjs/core'
import {CatalogItemCarouselStore, CatalogItemCarouselStoreContext, SidebarAlign, ImageSourceName, ImageParentId, useCatalogItemCarouselStore, verticalAlignIcons} from '@design-board/stores'
import {useStores, useVipLocalStore} from '@modules'
import {NoImagePlaceholder} from '@modules/catalog'
import {LoadingIndicator} from '@modules/common'
import {useCatalogItemSidebarDetailsStore} from '@modules'
import {useGesture} from '@use-gesture/react'
import {HTMLProps} from 'react'
import {DragDropContext, Droppable, Draggable} from 'react-beautiful-dnd'
import * as css from './CatalogItemImageCarousel.css'
import {ItemVerticalAlign, ui, useCatalog} from '@stores'
import {MyImage, DragEventPlaceholders, PrimaryIconButton} from 'centric-ui-components'
import {PlayCircleOutline, Fullscreen, DeleteOutlineOutlined} from '@material-ui/icons'
import {Tooltip2} from '@api'
import { Tooltip } from '@material-ui/core'

/**
 * Shows thumbnails and an active image with animation as the user clicks on different images.
 *
 * Desired (but missing!) features:
 *   Drag and drop movement of images - if moving from a nested object to a parent/sibling we should ask if we want to clone or replace.  If we do clone we should track who we cloned from and only display one the original in the carousel
 *   D&D and inline delete of items with confirm
 *   Drag image from desktop into new position with preview in the modal
 *   Label indicating where the image comes from (MPV, Color, Colorway, etc)
 *
 *   Right click open image in specific other workspace
 *   Right click to choose which items to display from (image source but only for modal?)
 *
 *   Ability to change the L&F
 * {@notes First developed for the modal dialog.}
 */
export const CatalogItemImageCarousel = observer<{ className?: string, orientation?: 'vertical' | 'horizontal', align?: SidebarAlign }>(function CatalogItemImageCarousel({className, orientation, align}) {
  const sidebarDetailsStore = useCatalogItemSidebarDetailsStore(),
    store = useVipLocalStore((ws) => new CatalogItemCarouselStore(ws, sidebarDetailsStore)),
    {
      details: {catalog: {stores: {strings}, placeholderImgFor}, loading, data, activeImageSource, localImages: _localImages, imageSourceById, placeholderImg, dragDropEvents},
      showThumbnails, selectedImage, selectedImageIndex, resetSelectedIndex, selectedImageSourceFieldName, ddHandlers,
      carousel_onChangeIndex, carouselAnimationInterval, isDragging, localImages, onDragLeaveImage, annotation, annotations, onSubmitAnnotation, onChangeAnnotation
    } = store

  useEffect(() => store.registerEffects(), [])

  const bind = useGesture({
    onWheel: state => {
      console.log(state)
      const v = state.delta[orientation === 'horizontal' ? 0  : 1]
      // console.log(`onWheel`, v)
      store.onSwipe_throttled(v)
    }
  }, {
    wheel: {
      axis: orientation === 'horizontal' ? 'x' : 'y',
      eventOptions: {

      }
    }
  })
  return (
    <CatalogItemCarouselStoreContext.Provider value={store}>
      <div className={classNames(className, css.root)} data-grid-area='carousel'
           data-show-thumbnails={showThumbnails}
           onDragEnter={onDragLeaveImage}
           data-orientation={orientation ?? 'vertical'}
           data-drag-start={isDragging}
           data-selected-index={selectedImageIndex}
           {...dragDropEvents.events}
        //onDoubleClick_forTabNode={toggleThumbnails}
      >
        {loading && !data ? <LoadingIndicator/>
                          : <Fragment>
           <CarouselIconList orientation='vertical'/>
           {selectedImage && <div
			   title={`${selectedImageSourceFieldName ?? selectedImage.source} Image`}
			   className={css.imageSourceLabel}>{_.first(selectedImageSourceFieldName)}</div>}
           {ddHandlers?.state?.status === 'loading' && <Spinner className={css.loadingSpinner} intent='primary' size={30}/>}
           <div className={css.carouselWrapper} {...bind()}>
           <ReactResponsiveCarousel className={css.carousel}
             //width='400px'
                     axis={orientation/* == 'vertical' ? 'horizontal' : 'vertical'*/}
                     showArrows={false}
                     showThumbs={false}
                     dynamicHeight={false}
                                    emulateTouch
                                    interval={carouselAnimationInterval}
                     useKeyboardArrows={true}
                     onChange={selectedImageIndex === -1 ? undefined : carousel_onChangeIndex} // The carousel will try and override us setting selected index to -1 and set it back to 0 - resulting in an infinite loop
                     selectedItem={selectedImageIndex === -1 ? 0 : selectedImageIndex}
           >
             {(_.isEmpty(localImages) ?
               [
                 <NoImagePlaceholder key='placeholder' placeholderImg={placeholderImgFor(data)} className={css.noImagePlaceholder} levelOfDetail={1}
                                     placeholderText={strings.noImage}/>
               ]
                                      : (localImages ?? []).map(
                 (image: ImageWrapperDefinition, index) => (<ModalCarouselImage key={index}
                                                                                image={image}/>)))}
           </ReactResponsiveCarousel>
           </div>
           <CarouselThumbnailList />
         </Fragment>}

        {false && <ImageCarouselImageSourceChooser/>}
        {/*<GlassMagnifier imageSrc={catalog.imageUrlFor(images[0], 'image')}/>*/}
      </div>
    </CatalogItemCarouselStoreContext.Provider>)
})

const ModalCarouselImage = observer<{ image: ImageWrapperDefinition & {__parent?: any} } & HTMLProps<HTMLDivElement>>(function ModalCarouselDraggableImage({image, ...props}) {
  const {catalog, catalog: {imageUrlFor}, details: {dragDropEvents}, annotations, annotation, contentsAlignSelf, workspaceForImageSource, onChangeAnnotation, onSubmitAnnotation} = useCatalogItemCarouselStore(),
    {source, __parent} = image,
    {ws, objectFit, c8ImageUrl, onDoubleClick} = useLocalStore(() => ({
      get ws() { return workspaceForImageSource(source) },
      get objectFit() { return this.ws?.catalog?.features?.card?.objectFit ?? 'contain'},
      get c8ImageUrl() { return imageUrlFor(image, {c8Size: 'image', source: 'c8'})},
      onDoubleClick: () => {
        const {findFieldInTree, idFor, sidebar: {right: {selectPathItem, selectedPathIndex}}} = catalog,
          catalogField = findFieldInTree(source, true),
          id = idFor(__parent, false)

        // console.log(toJS({image, __parent, source, catalogField, id}, {recurseEverything: true}))

        if (id && catalogField) {
          selectPathItem(selectedPathIndex + 1, {
            esIndex: catalogField.esIndex,
            where: [{value: id}],
            catalogField: catalogField?.field
          })
        }
      }
    }))

  // console.log(toJS({image, source, ws, data: details.data}, {recurseEverything: true}))

  return (<Fragment>
    <div className={css.carouselItem}
         data-object-fit={objectFit}
         data-source={image.source}
         data-id={image?.id ?? image?.exId}
         data-workspace={ws?._id}
         data-contents-align-self={contentsAlignSelf}
         {...props}
         onDoubleClick={onDoubleClick}
         style={{
           '--card-image-padding': objectFit == 'cover' ? undefined : ws?.catalog?.features?.card?.imagePadding,
           '--card-image-align-self': ws?.catalog?.features?.card?.alignSelf,
           '--card-image-contents-align-self': contentsAlignSelf,
         } as any}
    >
      <DragEventPlaceholders id={dragDropEvents.def.id} dropType={dragDropEvents.def.target}/>

      {ui.settings.experimental?.imageAnnotationInModal
       ? <ImageAnnotationComponent
         src={catalog.imageUrlFor(image, {c8Size: 'largeImage'})}
         annotations={annotations}
         // type={type}
         value={annotation}
         onChange={onChangeAnnotation}
         onSubmit={onSubmitAnnotation}
       />
       : <MyImage
          imageId={image.exId ?? image.id}
          transparent={ws?.catalog?.features?.autoTransparency === true}
          rawImageSize={1600}
         imageUrl={catalog.imageUrlFor(image, {autoTransparency: ws?.catalog?.features?.autoTransparency, c8Size: 'largeImage'})}
         c8ImageUrl={c8ImageUrl}
         loader={<LoadingIndicator type='placeholder'/>}/>}
      {/*<GlassMagnifier imageSrc={catalog.imageUrlFor(image, 'image')} />*/}
    </div>
  </Fragment>)
})

interface ICarouselThumbnailDroppableByParent {
  source: ImageSourceName
  parent: ImageParentId
}

const CarouselThumbnailDroppableByParent = observer<ICarouselThumbnailDroppableByParent>(function CarouselThumbnailDroppableByParent({
  source, parent
}) {
  const store = useCatalogItemCarouselStore(),
    { catalog: { idFor }, workspaceImagesBySourceAndParent, getItemStyle, getListStyle } = store,
    images = workspaceImagesBySourceAndParent[source][parent] || [],
    DROPPABLE_DELIMITER = '__',
    droppableId = [uiConstants.CAROUSEL_THUMBNAIL_DROPPABLE_PREFIX, source, parent].join(DROPPABLE_DELIMITER)

  if (!images.length) return null

  return (
    <Droppable droppableId={droppableId} type={droppableId}>
    {(provided, snapshot) => {
      store.isDragging = snapshot.draggingFromThisWith != null
      
      return (
        <Observer>
          {() => (
            <div
              ref={provided.innerRef}
              data-dragging={snapshot.draggingFromThisWith != null}
              style={getListStyle(snapshot.isDraggingOver)}
              {...provided.droppableProps}
            >
              {images.map((image, index) => {
                const imageKey = `image-${idFor(image) ?? index}`

                return (
                  <Draggable
                    index={index}
                    key={imageKey}
                    draggableId={imageKey}
                    // isDragDisabled={!allowImageReordering(image)}
                  >
                    {(provided, snapshot) => (
                      <Observer>
                        {() => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={getItemStyle(provided.draggableProps.style, snapshot.isDragging)}
                          >
                            <DraggableThumbnailImage image={image} index={index} />
                          </div>
                        )}
                      </Observer>
                    )}
                  </Draggable>
                )
              })}
              {provided.placeholder}
            </div>
          )}
        </Observer>
      )
    }}
  </Droppable>
  )
})

interface ICarouselThumbnailDroppableBySource {
  source: ImageSourceName
}

const CarouselThumbnailDroppableBySource = observer<ICarouselThumbnailDroppableBySource>(function CarouselThumbnailDroppableBySource({
  source
}) {
  const store = useCatalogItemCarouselStore(),
    { workspaceImagesBySourceAndParent } = store,
    imageListBySource = workspaceImagesBySourceAndParent[source] || {}

  if (!Object.keys(imageListBySource).length) return null

  return (
    <>
      {Object.keys(imageListBySource).map(parent => (
        <CarouselThumbnailDroppableByParent key={parent} source={source} parent={parent} />
      ))}
    </>
  )
})

const CarouselThumbnailList = observer(function CarouselThumbnailList() {
  const store = useCatalogItemCarouselStore(),
    { associatedWorkspaceImageSource, nonAssociatedWorkspaceImagesByGroup, details: { sidebar }, onThumbanilDragEnd } = store

  if (sidebar?.type === 'sidebar') return null

  return (
    <div className={css.carouselDragDropContainer}>
      <DragDropContext onDragEnd={onThumbanilDragEnd}>
        <CarouselThumbnailDroppableBySource source={associatedWorkspaceImageSource.field} />

        {Object.keys(nonAssociatedWorkspaceImagesByGroup).map(source => (
          <CarouselThumbnailDroppableBySource source={source} key={source} />
        ))}
      </DragDropContext>
    </div>
  )
})

const DraggableThumbnailImage = observer<{ image: ImageWrapperDefinition, index: number }>(function DraggableThumbnailImage({image, index}) {
  const {
      details: {canAddImageOnData, imageSources}, labelForImageSourceField, localImages, dragOverIndex, imageData,
      carousel_onChangeCarouselExId, thumbnail_onDoubleClick, onImageDrop, onDragStart, setThumbnailRef, allowImageReordering,
      selectedImage, workspaces: {workspaceForImageSource}, catalog, associatedWorkspace, imageSourceForImage
    } = useCatalogItemCarouselStore(),
    imageSource = imageSourceForImage(image),
    imageSourceWorkspace = workspaceForImageSource(imageSource, associatedWorkspace.catalog)

  const allowReorder: boolean = allowImageReordering(image),
    showPlaceholderForDrop = allowReorder && dragOverIndex == index && imageData === null,
    parentClass = [css.animateItemWrapper],
    objectFit = imageSourceWorkspace.objectFit ?? 'contain'

  showPlaceholderForDrop && parentClass.push(css.animateItemWrapperPlaceholderHeight)
  !allowReorder && (dragOverIndex == index) && parentClass.push(css.animateItemWrapperStyle)

  // console.log(toJS({objectFit, imageSourceConfig, imageSources, imageSource, index, image, imageSourceWorkspace: imageSourceWorkspace.id , associatedWorkspace: associatedWorkspace.id}, {recurseEverything: true}))

  return (
    <Tooltip title={`${labelForImageSourceField(image.source)} Image`} placement='right'>
      <div
        ref={setThumbnailRef(index)}
        data-index={index}
        data-field={image.source}
        data-object-fit={objectFit}
        data-associated-workspace={associatedWorkspace?.id}
        data-workspace={imageSourceWorkspace?._id}
        onDrop={(e) => canAddImageOnData ? onImageDrop(e, allowReorder) : undefined}
        className={classNames(parentClass.join(', '), css.thumbnailWrapper)}
        draggable={false}
        style={{
          '--card-image-padding': objectFit == 'cover' ? undefined : imageSourceWorkspace?.catalog?.features?.card?.imagePadding,
          '--card-image-align-self': imageSourceWorkspace?.catalog?.features?.card?.alignSelf,
          '--image-source-object-fit': objectFit
        } as any
        }
      >
        <MyImage key={index}
                 style={{
                   order: index === localImages.length - 1 ? 0 : 1
                 } as any}
                 data-index={index}
                 className={css.thumbnail}
                 lazy={false}
                 loader={<LoadingIndicator type='placeholder'/>}
                 noImageContent={<NoImagePlaceholder/>}
                 rawImageSize={400}
                 imageId={image.exId}
                 transparent={imageSourceWorkspace?.catalog?.features?.autoTransparency}
                 imageUrl={image ? catalog.imageUrlFor(image, {c8Size: 'smallImage', autoTransparency: imageSourceWorkspace?.catalog?.features?.autoTransparency}) : null}
                 onClick={() => carousel_onChangeCarouselExId(image?.exId)}
                 onDoubleClick={thumbnail_onDoubleClick(index)}
                 data-selected={!!selectedImage && selectedImage?.exId === image?.exId}
                 draggable={allowReorder}
        />
      </div>
    </Tooltip>)
})

export interface ICarousalIconProps extends StyledProps {
  icon: 'fullscreen' | 'trashcan' | 'play'
  onClick?: (e) => void
  onDrop?: (e) => void
  onDragOver?: (e) => void
  toolTip: string
  isDragOver?: boolean
  disabled?: boolean
  onDragLeave?: () => void
}

const CarouselIcon = observer<ICarousalIconProps>(function CarouselIcon(props: ICarousalIconProps) {
  const {icon, onClick, onDrop, toolTip, disabled, onDragOver, isDragOver, onDragLeave, className} = props,
    iconList = {
      fullscreen: Fullscreen,
      trashcan: DeleteOutlineOutlined,
      play: PlayCircleOutline
    },
    Icon = iconList[icon]
  return <div key={icon} className={classNames(className, css.carouselIcon)}>
    <Tooltip2
      data-id={icon}
      data-placeholder-drag-over={isDragOver}
      placement={'bottom'}
      content={toolTip}
      isOpen={isDragOver}>
      <PrimaryIconButton icon={<Icon fontSize='large' className={css.icon}/>}
                         onClick={onClick}
                         onDrop={onDrop}
                         onDragOver={onDragOver}
                         onDragLeave={onDragLeave}
                         disabled={disabled}
      >
      </PrimaryIconButton>
    </Tooltip2>
  </div>
})

const CatalogItemImageVerticalAlignmentDropdownButton = observer(
  function CatalogItemImageVerticalAlignmentDropdownButton() {
    const store = useCatalogItemCarouselStore(),
      {
        vAlign: currentAlign,
        changeVerticalAlign,
        verticalAlignIcon,
        strings,
      } = store

    return (
      <Popover2
        interactionKind='hover'
        content={
          <Menu>
            <MenuDivider title={strings.itemAlignment} />
            {['top', 'center', 'bottom', 'stretch'].map(
              (vAlign: ItemVerticalAlign) => (
                <MenuItem
                  key={vAlign}
                  shouldDismissPopover={false}
                  labelElement={<Icon icon={verticalAlignIcons[vAlign]} />}
                  icon={vAlign === currentAlign ? 'tick' : 'blank'}
                  text={_.capitalize(vAlign)}
                  onClick={() => changeVerticalAlign(vAlign)}
                  active={vAlign === currentAlign}
                />
              )
            )}
          </Menu>
        }
      >
        <PrimaryIconButton icon={verticalAlignIcon} />
      </Popover2>
    )
  }
)

export interface ICarouselIconListProps {
  orientation?: 'horizontal' | 'vertical',
  align?: 'left' | 'right'
}

const CarouselIconList = observer<ICarouselIconListProps>(function CarouselIconList(props) {
  const {orientation, align} = props,
    {strings, onDragOver, hasImages, removeCatalogItemImage_onDrop, removeCatalogItemImage_onClick, onDragLeave, onToggleAutoplay, addImages, disableDeleteImage, selectedImageIndex} = useCatalogItemCarouselStore(),
    {onClickViewImage, onTogglePresentationMode, presentationMode, canAddImageOnData} = useCatalogItemSidebarDetailsStore(),
    catalog = useCatalog()

  return <div className={classNames(css.iconListContainer, css[orientation])} data-type='CarouselIconList'>
    {false && <CarouselIcon icon='play' toolTip={strings.autoplay} onClick={onToggleAutoplay}/>}
    {hasImages && <CarouselIcon icon='fullscreen' toolTip={strings.fullscreenImage} onClick={onClickViewImage}/>}

    {hasImages && <Tooltip2 placement='right' content={strings.presentationMode}>
		<PrimaryIconButton icon={<Icon icon='presentation'/>}
		                   data-id='presentation'
		                   active={presentationMode}
		                   onClick={onTogglePresentationMode}
		/>
	</Tooltip2>}

    {hasImages && <CatalogItemImageVerticalAlignmentDropdownButton />}

    {catalog.canAddImage && canAddImageOnData && align !== 'left' && <AddImagePlaceHolder onChange={addImages}/>}
    {hasImages && catalog.canAddImage && canAddImageOnData && <CarouselIcon icon='trashcan' toolTip={disableDeleteImage ? selectedImageIndex < 0 ? '' : strings.disableRemoveImage(catalog) : strings.removeImageFromCarousel}
	                                                                        onClick={disableDeleteImage ? null : removeCatalogItemImage_onClick}
	                                                                        onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={removeCatalogItemImage_onDrop} className={css.trashcan} disabled={disableDeleteImage}/>
    }
  </div>
})

const ImageCarouselImageSourceChooser = observer(function ImageCarouselImageSourceChooser() {
  const {details: {imageSources, localImages, activeImageSource, toggleActiveImageSource}} = useCatalogItemCarouselStore(),
    catalog = useCatalog()

  return imageSources?.length < 1 ? null : (
    <div className={css.imageSourceChooser}>
      {imageSources.map((source, i) => {
        const {field} = source,
          label = source.label ?? catalog.labelForFieldName_currentCatalog(field, 'plural')

        return (
          <Tooltip2 key={i} content={`${label} (${localImages.filter(i => i.source == field).length})`} className={css.imageSourceButton}>
            <PrimaryIconButton
              key={i}
              icon={<span>{label[0]}</span>}
              active={activeImageSource == field}
              onClick={toggleActiveImageSource(field)}/>
          </Tooltip2>)
      })}
    </div>)
})

export const AddImagePlaceHolder = observer<{ onChange, className?: string }>(function AddImagePlaceHolder({onChange, className}) {
  const file = React.createRef<HTMLInputElement>()
  const onAdd = (e) => {
      e.stopPropagation()
      file?.current?.click()
    },
    {strings} = useStores()

  return (
    <div className={classNames(className, css.addImageIcon)}>
      <PrimaryIconButton
        tooltip={strings.addImage}
        data-id='addImage'
        icon={<Icon icon='add'/>} onClick={onAdd}/>
      <input type='file' accept={'.jpg, .jpeg, .png'} multiple={true} ref={file} style={{display: 'none'}}
             onChange={onChange}/>
    </div>)
}
)



// WEBPACK FOOTER //
// ./src/modules/CatalogItemDetails/CatalogItemSidebarDetails/CatalogItemImageCarousel.tsx